import { GetListParams, GetListResult, GetManyReferenceParams, GetManyReferenceResult, RaRecord } from 'react-admin';
import { getJson } from '../../../utils/api';
import IHandler from './IHandler';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(tz);

interface Transaction {
  userId: string;
  status: string;
  amount: number | null;
  provider: any;
  description: string;
  transactionId: string;
  cardId: string;
  type: string;
  createdAt: string;
}

interface ExtendedTransaction extends Transaction {
  debitAmount: number | null;
  creditAmount: number | null;
  balanceAfterTransaction: number | null;
  endOfWeekBalance: number | null;
}

export default class UserTransactionsHandler implements IHandler {
  static resourceIdName = 'transactionId';

  static async getListHandler<RecordType extends RaRecord = any>(
    resource?: string,
    params?: GetListParams
  ): Promise<GetListResult<RecordType>> {
    const query: { [key: string]: any } = {
      limit: params?.pagination.perPage ?? 10,
    };

    if (params?.filter.nextToken != null) {
      query.nextToken = params?.filter.nextToken;
    }

    const userId = params?.filter?.userId;

    return getJson(`/admin/user/${userId}/transactions?${new URLSearchParams(query).toString()}`).then(
      async (response) => {
        let { data: responseData } = await response.json();

        const nextToken = responseData.nextToken;

        responseData = responseData?.transactions || [];

        responseData = responseData.map((data: any) => {
          const result = {
            ...data,
          };

          result.id = data[this.resourceIdName];
          delete result[this.resourceIdName];

          return {
            ...result,
          };
        });

        return {
          data: responseData,
          pageInfo: {
            hasNextPage: !!nextToken,
          },
          nextToken,
        };
      }
    );
  }

  static async getManyReference<RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyReferenceParams
  ): Promise<GetManyReferenceResult<RecordType>> {
    const data: any[] = [];
    const weeklyPaymentDay = params?.filter?.weeklyPaymentDay;
    console.log(weeklyPaymentDay);

    let nextToken = null;
    do {
      const ret: any = await UserTransactionsHandler.getListHandler(resource, {
        filter: {
          userId: params?.id,
          ...params.filter,
          nextToken,
        },
        meta: params.meta,
        pagination: params.pagination,
        sort: params.sort,
      });
      console.log(ret);
      nextToken = ret.nextToken;
      data.push(...ret.data);
    } while (nextToken != null);

    return {
      data: UserTransactionsHandler.postProcessTransactions(data, weeklyPaymentDay ?? 2) as unknown as RecordType[],
      pageInfo: {
        hasNextPage: false,
      },
    };
  }

  static postProcessTransactions(data: Transaction[], weeklyPaymentDay: number): ExtendedTransaction[] {
    const transaction = data.reverse();

    const isCredit = (record: Transaction) =>
      ['reward-points-redemption', 'payment', 'transaction-type-card-receive'].includes(record.type);

    const isAdjustment = (record: Transaction) => ['adjustment'].includes(record.type);

    const getDebit = (record: Transaction) => {
      if (
        record.amount == null ||
        record.amount === 0 ||
        record.status === 'transaction-status-card-reservation-declined' ||
        record.status === 'transaction-status-card-reservation-approved' ||
        record.status === 'transaction-status-card-reservation-released'
      ) {
        return null;
      }

      if (isAdjustment(record)) {
        if (record.amount < 0) {
          return Math.abs(record.amount);
        } else {
          return null;
        }
      }

      if (isCredit(record)) {
        return null;
      }

      return Math.abs(record.amount);
    };

    const getCredit = (record: Transaction) => {
      if (
        record.amount == null ||
        record.amount === 0 ||
        record.status === 'transaction-status-card-reservation-declined' ||
        record.status === 'transaction-status-card-reservation-approved' ||
        record.status === 'transaction-status-card-reservation-released'
      ) {
        return null;
      }

      if (isAdjustment(record)) {
        if (record.amount > 0) {
          return Math.abs(record.amount);
        } else {
          return null;
        }
      }

      if (isCredit(record)) {
        return Math.abs(record.amount);
      }

      return null;
    };

    const ret: ExtendedTransaction[] = [];
    let runningBalance = 0;
    const addedWeeks = new Set<string>();
    for (const trx of transaction) {
      const trxDate = new Date(trx.createdAt);
      let currentWeekPaymentDayDate = dayjs.utc(trxDate).set('day', weeklyPaymentDay).toDate();

      if (trxDate >= currentWeekPaymentDayDate) {
        const markersToAdd: ExtendedTransaction[] = [];
        while (!addedWeeks.has(currentWeekPaymentDayDate.toISOString().substring(0, 10))) {
          // Insert a weekly balance marker
          const markerTrx: ExtendedTransaction = {
            userId: trx.userId,
            status: 'balance-marker',
            amount: null,
            provider: {},
            description: '',
            transactionId: `balance-marker-${trx.transactionId}`,
            cardId: '',
            type: 'balance-marker',
            createdAt: new Date(
              Date.UTC(
                currentWeekPaymentDayDate.getUTCFullYear(),
                currentWeekPaymentDayDate.getUTCMonth(),
                currentWeekPaymentDayDate.getUTCDate(),
                0,
                0,
                0
              )
            ).toISOString(),
            debitAmount: null,
            creditAmount: null,
            balanceAfterTransaction: null,
            endOfWeekBalance: runningBalance,
          };
          markersToAdd.push(markerTrx);
          addedWeeks.add(currentWeekPaymentDayDate.toISOString().substring(0, 10));
          currentWeekPaymentDayDate = new Date(currentWeekPaymentDayDate.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (addedWeeks.size === 1) {
            break;
          }
        }
        if (markersToAdd.length > 0) {
          ret.push(...markersToAdd.reverse());
        }
      }

      const debitAmount = getDebit(trx);
      const creditAmount = getCredit(trx);
      const extendedTrx: ExtendedTransaction = {
        ...trx,
        debitAmount,
        creditAmount,
        balanceAfterTransaction: 0,
        endOfWeekBalance: null,
      };

      if (debitAmount != null || creditAmount != null) {
        if (isAdjustment(trx)) {
          if (debitAmount != null) {
            runningBalance += debitAmount;
          } else if (creditAmount != null) {
            runningBalance -= creditAmount;
          }
        } else {
          if (isCredit(trx)) {
            runningBalance -= creditAmount!;
          } else {
            runningBalance += debitAmount!;
          }
        }
      }

      extendedTrx.balanceAfterTransaction = runningBalance;
      ret.push(extendedTrx);
    }
    return ret.reverse();
  }
}
